import { Injectable } from '@angular/core';
import { IMSId, LabelFormatSetting } from 'src/app/_model/ims-object';
import { Servicejob } from 'src/app/_model/servicejob';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';
import { LocalStatusService } from 'src/app/_services/local-status/local-status.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import {
  DbPersistenceService,
  Query,
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { KeyValuePersistenceService } from 'src/app/_services/persistence/key-value-persistence/key-value-persistence.service';
import { ServicejobsDaoService } from '../servicejobs-dao.service';
import { LocalServicejobDbTableDefinition } from './local-servicejobs-db-table-definition';
import { ServicejobDbTableDefinition } from './servicejobs-db-table-definition';

// CONSTANTS
const FINISHWITHNEXTSTATUSCHANGE_KEY =
  'servicejob_finish_with_next_status_change';
const NEXTTOURJOBNOTDELIVED_KEY = 'tourjob_finish_with_not_delivered';

@Injectable()
export class ServicejobsDbDaoService implements ServicejobsDaoService {
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private keyValueService: KeyValuePersistenceService,
    private localStatusService: LocalStatusService,
    private appParameter: AppParameterService,
    private servicejobTableDefinition: ServicejobDbTableDefinition,
    private localServicejobTableDefinition: LocalServicejobDbTableDefinition
  ) {}

  public async setFinisWithNextServicejobStatusChange(
    finish: boolean
  ): Promise<void> {
    await this.keyValueService.store(FINISHWITHNEXTSTATUSCHANGE_KEY, finish);
  }
  public getFinishWithNextServicejobStatusChange(): Promise<boolean> {
    return this.keyValueService.load(FINISHWITHNEXTSTATUSCHANGE_KEY);
  }

  public async setNextTourJobEndNotDelivered(
    notDelivered: boolean
  ): Promise<void> {
    await this.keyValueService.store(NEXTTOURJOBNOTDELIVED_KEY, notDelivered);
  }
  public async getTourJobStatusEndNotDelivered(): Promise<boolean> {
    return (
      (await this.keyValueService.load(NEXTTOURJOBNOTDELIVED_KEY)) ?? false
    );
  }

  public async saveServicejobs(servicejobs: Servicejob[]): Promise<void> {
    try {
      let db = await this.dbPersistenceService.getImsDb();

      var servicejobQueries: Query[] = [];
      for (const servicejob of servicejobs) {
        let servicejobQuery: Query = {
          statement:
            'INSERT OR REPLACE INTO ' +
            (servicejob.isLocal()
              ? this.localServicejobTableDefinition.tableName
              : this.servicejobTableDefinition.tableName) +
            '(id, label, databag, keywordlist, description, priority, status, ' + //7
            ' validpersonstring, contactinfo, validphonenumber, ' + // 3
            ' validmail, validlatitude, validlongitude, validmatchingradius, ' + // 4
            ' employeeid, employeelabel, equipmentid, equipmentlabel,' + // 4
            ' branchid, branchlabel, locationid, locationlabel, accountingnumber, ' + //5
            ' name, projsupervisorid, projsupervisorlabel, supervisorid, supervisorlabel, ' + //5
            ' plannedtime, additionalinfotext, ' + // 2
            ' branchname, branchfullstreet, branchcity, createdtstamp, ' + // 4
            ' geodataisaddresspreciseormanual, ' + // 1
            ' pcdecity, fullstreet, appaccountingnumber, validcustomername, ispermanentjob,' + // 5
            ' tourordernumber, tourid, tourlabel, tourstopid, tourstoplabel, ' + // 5
            ' tagtemplatevalidid, tagtemplatevalidlabel, geodataismanual, customerbranchid, customerbranchlabel, recipientbranchid, recipientbranchlabel)' + // 7
            ' VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)',
          parameters: [
            Number(
              servicejob.isLocal()
                ? servicejob.id.secondaryId
                : servicejob.id.imsId
            ),
            servicejob.label,
            servicejob.databag ? JSON.stringify(servicejob.databag) : undefined,
            servicejob.keywordlist.join(','),
            servicejob.description,
            servicejob.priority,
            servicejob.status,
            servicejob.validpersonstring,
            servicejob.contactinfo,
            servicejob.validphonenumber,
            servicejob.validmail,
            servicejob.validlatitude,
            servicejob.validlongitude,
            servicejob.validmatchingradius,
            servicejob.employee?.id?.imsId
              ? Number(servicejob.employee.getImsId())
              : undefined,
            servicejob.employee?.id?.imsId ? servicejob.employee.label : undefined,
            servicejob.equipment?.id?.imsId
              ? Number(servicejob.equipment.getImsId())
              : undefined,
            servicejob.equipment?.id?.imsId ? servicejob.equipment.label : undefined,
            servicejob.branch?.id?.imsId
              ? Number(servicejob.branch.getImsId())
              : undefined,
            servicejob.branch ? servicejob.branch.label : undefined,
            servicejob.location?.id?.imsId
              ? Number(servicejob.location.getImsId())
              : undefined,
            servicejob.location?.id?.imsId ? servicejob.location.label : undefined,
            servicejob.accountingnumber,
            servicejob.name,
            servicejob.projsupervisor?.id?.imsId
              ? Number(servicejob.projsupervisor.getImsId())
              : undefined,
            servicejob.projsupervisor?.id?.imsId
              ? servicejob.projsupervisor.label
              : undefined,
            servicejob.supervisor?.id?.imsId
              ? Number(servicejob.supervisor.getImsId())
              : undefined,
            servicejob.supervisor?.id?.imsId ? servicejob.supervisor.label : undefined,
            servicejob.plannedtime,
            servicejob.additionalinfotext,
            servicejob.branchname,
            servicejob.branchfullstreet,
            servicejob.branchcity,
            servicejob.createdtstamp,
            servicejob.geodataisaddresspreciseormanual,
            servicejob.pcdecity,
            servicejob.fullstreet,
            servicejob.appaccountingnumber,
            servicejob.validcustomername,
            servicejob.ispermanentjob ? servicejob.ispermanentjob : false,
            servicejob.tourordernumber,
            servicejob.tour?.id?.imsId ? Number(servicejob.tour.getImsId()) : undefined,
            servicejob.tour?.id?.imsId ? servicejob.tour.label : undefined,
            servicejob.tourStop?.id?.imsId
              ? Number(servicejob.tourStop.getImsId())
              : undefined,
            servicejob.tourStop?.id?.imsId ? servicejob.tourStop.label : undefined,
            servicejob.tagtemplatevalid?.getImsId(),
            servicejob.tagtemplatevalid?.label,
            servicejob.geodataismanual ?? false,
            servicejob.customerbranch?.id?.imsId
              ? Number(servicejob.customerbranch.getImsId())
              : undefined,
            servicejob.customerbranch?.id?.imsId
              ? servicejob.customerbranch.label
              : undefined,
            servicejob.recipientbranch?.id?.imsId
              ? Number(servicejob.recipientbranch.getImsId())
              : undefined,
            servicejob.recipientbranch?.id?.imsId
              ? servicejob.recipientbranch.label
              : undefined,
          ],
        };
        await this.localStatusService.storeLocalServicejobStatus(servicejob);
        servicejobQueries.push(servicejobQuery);
      }
      await this.dbPersistenceService.executeBatch(db, servicejobQueries);
    } catch (error) {
      this.logger.log(
        'Error saving servicejobs to db',
        error,
        '-',
        error?.message
      );
    }
  }

  public async createServiceJob(servicejob: Servicejob): Promise<Servicejob> {
    this.logger.log(this.constructor.name, 'create sjob:', servicejob);
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let parameters = [
        servicejob.label,
        servicejob.databag ? JSON.stringify(servicejob.databag) : undefined,
        servicejob.keywordlist.join(','),
        servicejob.description,
        servicejob.priority,
        servicejob.status,
        servicejob.validpersonstring,
        servicejob.contactinfo,
        servicejob.validphonenumber,
        servicejob.validmail,
        servicejob.validlatitude,
        servicejob.validlongitude,
        servicejob.validmatchingradius,
        servicejob.employee
          ? Number(servicejob.employee.getImsId())
          : undefined,
        servicejob.employee ? servicejob.employee.label : undefined,
        servicejob.equipment
          ? Number(servicejob.equipment.getImsId())
          : undefined,
        servicejob.equipment ? servicejob.equipment.label : undefined,
        servicejob.branch ? Number(servicejob.branch.getImsId()) : undefined,
        servicejob.branch ? servicejob.branch.label : undefined,
        servicejob.location
          ? Number(servicejob.location.getImsId())
          : undefined,
        servicejob.location ? servicejob.location.label : undefined,
        servicejob.accountingnumber,
        servicejob.name,
        servicejob.projsupervisor
          ? Number(servicejob.projsupervisor.getImsId())
          : undefined,
        servicejob.projsupervisor ? servicejob.projsupervisor.label : undefined,
        servicejob.supervisor
          ? Number(servicejob.supervisor.getImsId())
          : undefined,
        servicejob.supervisor ? servicejob.supervisor.label : undefined,
        servicejob.plannedtime,
        servicejob.additionalinfotext,
        servicejob.branchname,
        servicejob.branchfullstreet,
        servicejob.branchcity,
        servicejob.createdtstamp,
        servicejob.geodataisaddresspreciseormanual,
        servicejob.pcdecity,
        servicejob.fullstreet,
        servicejob.appaccountingnumber,
        servicejob.validcustomername,
        servicejob.ispermanentjob,
        servicejob.tourordernumber,
        servicejob.tour ? Number(servicejob.tour.getImsId()) : undefined,
        servicejob.tour ? servicejob.tour.label : undefined,
        servicejob.tourStop
          ? Number(servicejob.tourStop.getImsId())
          : undefined,
        servicejob.tourStop ? servicejob.tourStop.label : undefined,
        servicejob.tagtemplatevalid?.getImsId(),
        servicejob.tagtemplatevalid?.label,
        servicejob.geodataismanual ?? false,
        servicejob.customerbranch
          ? Number(servicejob.customerbranch.getImsId())
          : undefined,
        servicejob.customerbranch ? servicejob.customerbranch.label : undefined,
        servicejob.recipientbranch
          ? Number(servicejob.recipientbranch.getImsId())
          : undefined,
        servicejob.recipientbranch
          ? servicejob.recipientbranch.label
          : undefined,
      ];
      let values =
        '(?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)';
      let fields =
        '(label, databag, keywordlist, description, priority, status, ' + //6
        ' validpersonstring, contactinfo, validphonenumber, ' + // 3
        ' validmail, validlatitude, validlongitude, validmatchingradius, ' + // 4
        ' employeeid, employeelabel, equipmentid, equipmentlabel,' + // 4
        ' branchid, branchlabel, locationid, locationlabel, accountingnumber, ' + //5
        ' name, projsupervisorid, projsupervisorlabel, supervisorid, supervisorlabel, ' + //5
        ' plannedtime, additionalinfotext, ' + // 2
        ' branchname, branchfullstreet, branchcity, createdtstamp, ' + // 4
        ' geodataisaddresspreciseormanual, ' + // 1
        ' pcdecity, fullstreet, appaccountingnumber, validcustomername, ispermanentjob, tourordernumber, tourid, tourlabel, tourstopid, tourstoplabel, ' + // 10
        ' tagtemplatevalidid, tagtemplatevalidlabel, geodataismanual, customerbranchid, customerbranchlabel, recipientbranchid, recipientbranchlabel)'; // 2
      let table = this.localServicejobTableDefinition.tableName;

      let sjobQuery: Query = {
        statement:
          'INSERT OR REPLACE INTO ' +
          table +
          ' ' +
          fields +
          ' VALUES ' +
          values,
        parameters: parameters,
      };
      await this.dbPersistenceService.executeStatement(db, sjobQuery);

      let res = await this.dbPersistenceService.executeQuery(db, {
        statement: 'select last_insert_rowid() as id',
        parameters: [],
      });
      if (res && res.values) {
        servicejob.id.secondaryId = res.values.shift()?.id;
        this.logger.log(
          'Updated servicejob secondary id',
          servicejob.id.secondaryId
        );
      }
      return servicejob;
    } catch (error) {
      this.logger.log(
        'Error saving servicejob to db',
        error,
        '-',
        error?.message
      );
      return undefined;
    }
  }

  public async getServicejobs(): Promise<Servicejob[]> {
    let db = await this.dbPersistenceService.getImsDb();
    let servicejobs: Servicejob[] = [];

    try {
      // Get Synced Servicejobs
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT * FROM ' + this.servicejobTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < data.values.length; i++) {
        let row = data.values[i];
        let localStatus =
          await this.localStatusService.loadLocalServicejobStatus({
            imsId: row['id'],
            secondaryId: undefined,
            objRef: 'ims/ServiceJob',
          });
        servicejobs.push(
          Servicejob.fromDto(
            {
              id: row['id'],
              secondaryid: undefined,
              label: row['label'],
              keywordlist: row['keywordlist'],
              description: row['description'],
              priority: row['priority'],
              status: row['status'],
              validpersonstring: row['validpersonstring'],
              contactinfo: row['contactinfo'],
              validphonenumber: row['validphonenumber'],
              validmail: row['validmail'],
              validlatitude: row['validlatitude'],
              validlongitude: row['validlongitude'],
              validmatchingradius: row['validmatchingradius'],
              employee: {
                id: row['employeeid'],
                label: row['employeelabel'],
              },
              equipment: {
                id: row['equipmentid'],
                label: row['equipmentlabel'],
              },
              branch: {
                id: row['branchid'],
                label: row['branchlabel'],
              },
              customerbranch: {
                id: row['customerbranchid'],
                label: row['customerbranchlabel'],
              },
              recipientbranch: {
                id: row['recipientbranchid'],
                label: row['recipientbranchlabel'],
              },
              location: {
                id: row['locationid'],
                label: row['locationlabel'],
              },
              pcdecity: row['pcdecity'],
              fullstreet: row['fullstreet'],
              accountingnumber: row['accountingnumber'],
              name: row['name'],
              projsupervisor: {
                id: row['projsupervisorid'],
                label: row['projsupervisorlabel'],
              },
              supervisor: {
                id: row['supervisorid'],
                label: row['supervisorlabel'],
              },
              plannedtime: row['plannedtime'],
              additionalinfotext: row['additionalinfotext'],
              branchname: row['branchname'],
              branchfullstreet: row['branchfullstreet'],
              branchcity: row['branchcity'],
              createdtstamp: row['createdtstamp'],
              geodataisaddresspreciseormanual:
                row['geodataisaddresspreciseormanual'] === 1,
              geodataismanual: row['geodataismanual'] === 1,
              appaccountingnumber: row['appaccountingnumber'],
              validcustomername: row['validcustomername'],
              ispermanentjob: row['ispermanentjob'] === 1,
              tourordernumber: row['tourordernumber'],
              tour: {
                id: row['tourid'],
                label: row['tourlabel'],
              },
              tourstop: {
                id: row['tourstopid'],
                label: row['tourstoplabel'],
              },
              tagtemplatevalid: {
                id: row['tagtemplatevalidid'],
                label: row['tagtemplatevalidlabel'],
              },
            },
            localStatus,
            row['databag'] ? JSON.parse(row['databag']) : undefined,
            this.appParameter.getFormatLabelForObject('ims/ServiceJob')
          )
        );
      }
    } catch (error) {
      this.logger.log('Error reading servicejob from db', error);
    }

    try {
      // Get local generated servicejobs
      let localData = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT * FROM ' +
          this.localServicejobTableDefinition.tableName +
          ';',
        parameters: null,
      });
      if (localData) {
        for (let i = 0; i < localData.values.length; i++) {
          let row = localData.values[i];
          let localStatus =
            await this.localStatusService.loadLocalServicejobStatus({
              imsId: row['id'],
              secondaryId: undefined,
              objRef: 'ims/ServiceJob',
            });
          const sjob = Servicejob.fromDto(
            {
              id: undefined,
              secondaryid: row['id'],
              label: row['label'],
              keywordlist: row['keywordlist'],
              description: row['description'],
              priority: row['priority'],
              status: row['status'],
              validpersonstring: row['validpersonstring'],
              contactinfo: row['contactinfo'],
              validphonenumber: row['validphonenumber'],
              validmail: row['validmail'],
              validlatitude: row['validlatitude'],
              validlongitude: row['validlongitude'],
              validmatchingradius: row['validmatchingradius'],
              employee: {
                id: row['employeeid'],
                label: row['employeelabel'],
              },
              equipment: {
                id: row['equipmentid'],
                label: row['equipmentlabel'],
              },
              branch: {
                id: row['branchid'],
                label: row['branchlabel'],
              },
              customerbranch: {
                id: row['customerbranchid'],
                label: row['customerbranchlabel'],
              },
              recipientbranch: {
                id: row['recipientbranchid'],
                label: row['recipientbranchlabel'],
              },
              location: {
                id: row['locationid'],
                label: row['locationlabel'],
              },
              pcdecity: row['pcdecity'],
              fullstreet: row['fullstreet'],
              accountingnumber: row['accountingnumber'],
              name: row['name'],
              projsupervisor: {
                id: row['projsupervisorid'],
                label: row['projsupervisorlabel'],
              },
              supervisor: {
                id: row['supervisorid'],
                label: row['supervisorlabel'],
              },
              plannedtime: row['plannedtime'],
              additionalinfotext: row['additionalinfotext'],
              branchname: row['branchname'],
              branchfullstreet: row['branchfullstreet'],
              branchcity: row['branchcity'],
              createdtstamp: row['createdtstamp'],
              geodataisaddresspreciseormanual:
                row['geodataisaddresspreciseormanual'] === 1,
              geodataismanual: row['geodataismanual'] === 1,
              appaccountingnumber: row['appaccountingnumber'],
              validcustomername: row['validcustomername'],
              ispermanentjob: row['ispermanentjob'] === 1,
              tourordernumber: row['tourordernumber'],
              tour: {
                id: row['tourid'],
                label: row['tourlabel'],
              },
              tourstop: {
                id: row['tourstopid'],
                label: row['tourstoplabel'],
              },
              tagtemplatevalid: {
                id: row['tagtemplatevalidid'],
                label: row['tagtemplatevalidlabel'],
              },
            },
            localStatus,
            row['databag'] ? JSON.parse(row['databag']) : undefined,
            this.appParameter.getFormatLabelForObject('ims/ServiceJob')
          );
          servicejobs.push(sjob);
        }
      }
    } catch (error) {
      this.logger.log('Error reading local servicejob from db', error);
    }
    return servicejobs;
  }

  public async removeServicejob(id: IMSId): Promise<void> {
    let db = await this.dbPersistenceService.getImsDb();
    try {
      await this.dbPersistenceService.executeStatement(db, {
        statement:
          'DELETE FROM ' +
          this.servicejobTableDefinition.tableName +
          ' WHERE id = (?);',
        parameters: [id.imsId],
      });
    } catch (error) {
      this.logger.log('Error deleting local servicejob', id, 'from db', error);
    }
  }

  public async deleteLocalServicejob(id: IMSId): Promise<void> {
    let db = await this.dbPersistenceService.getImsDb();
    try {
      await this.dbPersistenceService.executeStatement(db, {
        statement:
          'DELETE FROM ' +
          this.localServicejobTableDefinition.tableName +
          ' WHERE id = (?);',
        parameters: [id.secondaryId],
      });
    } catch (error) {
      this.logger.log('Error deleting local servicejob', id, 'from db', error);
    }
  }

  public async reset() {
    let db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.localServicejobTableDefinition.tableName
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.localServicejobTableDefinition.tableName
    );
    this.keyValueService.delete(FINISHWITHNEXTSTATUSCHANGE_KEY);
  }

  public async clearRemoteServicejobs() {
    let db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.servicejobTableDefinition.tableName
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.servicejobTableDefinition.tableName
    );
  }

  public async clear() {
    let db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.servicejobTableDefinition.tableName
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.servicejobTableDefinition.tableName
    );
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.localServicejobTableDefinition.tableName
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.localServicejobTableDefinition.tableName
    );
    this.logger.log('### sJOB clear');
    this.keyValueService.delete(FINISHWITHNEXTSTATUSCHANGE_KEY);
  }
}
